import { HttpRetryConfig } from './../../app/config/config.type';
import { Injectable, Injector, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, timer, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastService, SawDust } from '@cudirect/shared-ui-library';
import { RuntimeConfigService } from '@app/service/runtime-config.service';

/**
 * HTTP Retry Interceptor with exponential backoff and intelligent route filtering.
 *
 * Automatically retries failed HTTP requests for transient network errors, improving application
 * resilience and user experience. Essential for modern web applications due to:
 *
 * - Network instability (mobile networks, WiFi handoffs, intermittent connectivity)
 * - Microservice architecture complexity and cascading failures
 * - Cloud infrastructure transient issues (Azure service blips)
 * - Load balancer/API Gateway temporary unavailability
 * - CDN edge node failures and DNS resolution delays
 *
 * Industry best practices (Google SRE, AWS Well-Architected, Microsoft Azure patterns)
 * recommend client-side retry logic as a critical reliability pattern.
 *
 * RETRY STRATEGY:
 *
 * SHOULD RETRY:
 * - Network failures (status 0): timeouts, connection refused, DNS failures
 * - Server errors (5xx): internal server error, bad gateway, service unavailable, gateway timeout
 * - Rate limiting (429): too many requests, with appropriate backoff
 * - Specific client errors (408): request timeout from server
 * - Idempotent operations: GET, PUT, DELETE, HEAD, OPTIONS
 * - Safe operations that don't modify server state
 *
 * SHOULD NOT RETRY:
 * - Authentication errors (401, 403): invalid credentials, insufficient permissions
 * - Client errors (4xx except 408, 429): bad request, not found, method not allowed
 * - Resource conflicts (409): concurrent modification conflicts
 * - Payload issues (413, 414): request too large, URI too long
 * - Non-idempotent operations: POST requests that create resources
 * - Critical user flows: login, logout, password changes, payment processing
 * - File uploads: risk of duplicate uploads and resource consumption
 * - Application state changes: form submissions, data mutations
 *
 * ROUTE-SPECIFIC CONSIDERATIONS:
 * - API endpoints: generally safe to retry if idempotent
 * - Authentication flows: never retry to avoid account lockouts
 * - Financial transactions: never retry to prevent duplicate charges
 * - Notification services: avoid retry to prevent duplicate messages
 * - Analytics/tracking: may retry with caution to avoid inflated metrics
 *
 * Features:
 * - Minimal exponential backoff (125% growth) with jitter
 * - Configurable retry attempts, delays, status codes, and route filtering
 * - Zero overhead for successful requests (headers only added on retries)
 * - Toast notifications for user feedback during retry attempts
 *
 * TODO:
 * - More graceful ui/ux implementation of error messaging (toast should persist until retry attempts have completed)
 * - Add success logging when retry attempts succeed (perhaps using tap operator for tracking purposes)
 */
@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  // Injector is required due to potential circular dependency with ToastService
  constructor(private inj: Injector) {}

  // SawDust logger with retry interceptor context
  private logger = inject(SawDust).withContext({ component: 'RetryInterceptor' });

  /** Application config for overrides (if any), none by default */
  private retryConfig: HttpRetryConfig;

  /** Master switch - is retry enabled */
  private retryEnabled = false;

  /** Number of retry attempts to make */
  private retryAttempts = 3;

  /** Base delay between retry attempts in milliseconds */
  private retryDelayMs = 2500;

  /** HTTP status codes that trigger retry attempts */
  private retryStatusCodes: string[] = ['0', '400', '500'];

  /**
   * Routes that should NOT be retried (case-insensitive partial matches)
   * NOTE: This supersedes "included" routes
   */
  private retryExcludedRoutes: string[] = [
    'integration/GeneratePasscode',
    'integration/ValidatePasscode',
    'InstaTouch/CreatePartialApp',
    'SubmitOLAApplication/Check',
    'account/logout',
    'redirect/getPrefillOtuTokenData',
    'account/verifypasswordless',
    'account/startpasswordless'
  ];

  /** Routes that SHOULD be retried (case-insensitive partial matches) */
  private retryIncludedRoutes: string[] = ['cpapi'];

  /** HTML content for retry notification toast */
  private retryToastMessage = `<span class="bl-font-color--white">Retrying...</span>`;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.attemptRequest(request, next, 0);
  }

  /**
   * Attempts to execute the HTTP request with retry logic
   *
   * Uses recursion to handle retries: each retry creates a new call to attemptRequest
   * with an incremented attemptCount. This approach maintains clean separation between
   * attempts and allows each retry to be treated as a fresh request with its own context.
   *
   * @param request the HTTP request
   * @param next the next handler in the chain
   * @param attemptCount the current attempt count (0-based)
   * @returns Observable of HttpEvent
   */
  private attemptRequest(request: HttpRequest<unknown>, next: HttpHandler, attemptCount: number): Observable<HttpEvent<unknown>> {
    // Use original request for first attempt (zero overhead)
    // Add retry headers for subsequent attempts (telemetry exclusion)
    const requestToSend = attemptCount === 0 ? request : this.addRetryHeaders(request, attemptCount);

    return next.handle(requestToSend).pipe(
      catchError((error: HttpErrorResponse) => {
        const shouldRetry = this.shouldRetry(error, attemptCount, request);

        if (shouldRetry) {
          // Calculate exponential backoff delay with jitter
          const delayMs = this.calculateRetryDelay(attemptCount);

          // Show toast notification (-50ms for fade animation timing)
          if (this.retryToastMessage) {
            this.inj.get(ToastService).open(this.retryToastMessage, delayMs - 100);
          }

          this.logger.error(`Retrying HTTP request: ${request.url}... (attempt: ${attemptCount + 1})`);

          // Delay and recursively retry the request
          return timer(delayMs).pipe(switchMap(() => this.attemptRequest(request, next, attemptCount + 1)));
        } else {
          // Only log if we actually attempted retries (attemptCount > 0)
          // Otherwise it's just a first-attempt failure that's not eligible for retry
          if (attemptCount > 0) {
            this.logger.error(`HTTP request failed after ${attemptCount + 1} attempts: ${request.url}`, {
              url: request.url,
              totalAttempts: attemptCount + 1,
              finalErrorStatus: error.status,
              finalErrorMessage: error.message,
              errorDetails: error.error
            });
          }

          // Propagate the error up the chain
          return throwError(() => error);
        }
      })
    );
  }

  /**
   * Determines if the request should be retried based on configuration and error conditions
   * @param error the HTTP error response
   * @param retryIndex the current retry index (0-based)
   * @param request the original request
   * @returns boolean indicating if retry should occur
   */
  private shouldRetry(error: HttpErrorResponse, retryIndex: number, request: HttpRequest<unknown>): boolean {
    if (!this.retryConfig) {
      // Load retry configuration from runtime config service
      this.retryConfig = this.inj.get(RuntimeConfigService).getConfig?.httpRetryConfig;

      // Cannot safely retry without configuration
      if (!this.retryConfig) return false;
      else this.mapHttpRetryConfig(this.retryConfig);
    }

    // Check master retry switch
    if (!this.retryEnabled) return false;

    // Cache lowercase URL for route matching operations
    const lowerCaseRequestURL = request.url.toLowerCase();

    // Check if URL is explicitly excluded from retries
    if (this.retryExcludedRoutes.some((route) => lowerCaseRequestURL.includes(route.toLowerCase()))) return false;

    // Check if URL is in the allowed retry routes
    if (!this.retryIncludedRoutes.some((route) => lowerCaseRequestURL.includes(route.toLowerCase()))) return false;

    // Check if we've exhausted retry attempts (0-based indexing)
    if (retryIndex >= this.retryAttempts) return false;

    // Check if error status code is eligible for retry
    if (!this.retryStatusCodes.includes(error.status.toString())) return false;

    return true;
  }

  /**
   * Adds headers to the request to identify it as a retry attempt
   * This allows the API to selectively exclude retried requests from telemetry
   * @param request the HTTP request to modify
   * @param attemptNumber the current retry attempt number (1-based)
   * @returns the modified HTTP request with retry headers
   * @remarks Consider adding 'X-Original-Request-Id' header to correlate original and retry requests
   */
  private addRetryHeaders(request: HttpRequest<unknown>, attemptNumber: number): HttpRequest<unknown> {
    // Clone request and add retry identification headers for telemetry exclusion
    return request.clone({
      setHeaders: {
        'X-Is-Retry': 'true',
        'X-Retry-Attempt': attemptNumber.toString()
      }
    });
  }

  /**
   * Maps configuration values to instance properties, applying defaults where needed
   * @param config the application retry configuration
   */
  private mapHttpRetryConfig(config: HttpRetryConfig): void {
    this.retryEnabled = this.isTruthy(config?.enabled); // Master switch
    if (this.isTruthy(config?.attempts)) this.retryAttempts = +config.attempts; // Convert to number
    if (this.isTruthy(config?.delayMs)) this.retryDelayMs = +config.delayMs; // Convert to number
    if (this.isTruthy(config?.statusCodes)) this.retryStatusCodes = config.statusCodes.split(','); // CSV parsing
    if (this.isTruthy(config?.excludedRoutes)) this.retryExcludedRoutes = config.excludedRoutes.split(','); // CSV parsing
    if (this.isTruthy(config?.includedRoutes)) this.retryIncludedRoutes = config.includedRoutes.split(','); // CSV parsing
    if (this.isTruthy(config?.toastMessage) && config?.toastMessage !== 'hide') this.retryToastMessage = config.toastMessage;
    else if (config?.toastMessage === 'hide') this.retryToastMessage = null; // Explicit toast hiding
  }

  /**
   * Evaluates string values for truthiness, supporting pipeline configuration patterns
   * Treats "null" and "false" strings as falsy to support environment variable configuration
   * @param s the input string to evaluate
   * @returns false if s is falsy, "null", or "false"; true otherwise
   */
  private isTruthy(s: string): boolean {
    return !s || s.toLowerCase() === 'null' || s.toLowerCase() === 'false' ? false : true;
  }

  /**
   * Calculates retry delay using minimal exponential backoff with jitter
   * Formula: baseDelay * (1.25 ^ attemptCount) + jitter (capped at 30s)
   *
   * Examples with 2.5s base delay:
   * - Attempt 1: ~2.5s (2,250-2,750ms with jitter)
   * - Attempt 2: ~3.1s (2,813-3,438ms with jitter)
   * - Attempt 3: ~3.9s (3,516-4,297ms with jitter)
   *
   * @param attemptCount the current attempt count (0-based)
   * @returns delay in milliseconds (capped at 30 seconds)
   */
  private calculateRetryDelay(attemptCount: number): number {
    // Configuration constants for backoff behavior
    const BACKOFF_MULTIPLIER = 1.25; // 125% growth per attempt
    const JITTER_PERCENTAGE = 0.1; // ±10% jitter
    const MAX_DELAY_MS = 30000; // Cap at 30 seconds to prevent excessive waits

    // Calculate exponential backoff delay
    const exponentialDelay = this.retryDelayMs * Math.pow(BACKOFF_MULTIPLIER, attemptCount);

    // Apply jitter to prevent thundering herd effects
    const jitterRange = exponentialDelay * JITTER_PERCENTAGE;
    // Transform Math.random() [0,1] → [-0.5,0.5] → [-1,1] → [-jitterRange,+jitterRange]
    const jitter = (Math.random() - 0.5) * 2 * jitterRange;

    // Apply maximum delay cap for reasonable user experience
    return Math.min(Math.round(exponentialDelay + jitter), MAX_DELAY_MS);
  }
}
